// One "tap the POP tag" step on the engineer's job sheet (Session APF). "POP" is Proof Of Presence:
// the NFC sticker on the paper log card in a lift's motor room.
//
// WHAT THE DIRECTOR ASKED FOR (21 September 2026). The tag becomes an ON/OFF PAIR inside the job
// itself, for a service visit, a call-out and a repair alike: tap it to confirm you have arrived at
// the start of the sheet, tap it again to confirm you are leaving at the end. The two tap times
// become the DEFAULT arrival and departure time of the job. So this one compact card is rendered
// TWICE by JobDetail.tsx, once per direction, and each tap hands its time up through `onStamp`.
//
// WHAT THIS CARD IS, AND ALL IT IS: a way of keeping time. It records a tap and pre-fills a time
// field. It is deliberately not part of any other system on the sheet, it says nothing about the
// engineer's welfare, and no wording here may suggest that it does. The separation test beside this
// file reads this source and fails if that ever stops being true, and its header carries the
// Director's reason in full.
//
// THREE RULES THAT SHAPE EVERYTHING BELOW.
//
//   IT NEVER BLOCKS. A missing tap must never stop a sheet being submitted, so nothing on this card
//   disables or gates anything else, and the card says so itself ("Carry on and set the time
//   yourself"). A phone that cannot read a tag at all renders nothing here, and the parent renders
//   nothing for a lift with no tag fitted.
//
//   IT PRE-FILLS, IT NEVER OVERRIDES. The card reports the time of the tap and the parent decides
//   what to do with it: fill the Arrived or Left field if it is empty, and otherwise leave what the
//   engineer typed alone. This component never touches a time field.
//
//   THE FIRST TAP OF A NEWLY FITTED STICKER IS NOT A VISIT, IN EITHER DIRECTION. The server records
//   that tap as an install confirmation and excludes it from every count (Session AOE). This card
//   must agree with it, or the first job on every newly tagged lift would take its arrival time from
//   a tap the platform itself says was not an attendance. So an install confirmation stamps nothing
//   and asks for one more tap, which the server then records as the real one straight away.
//
// The scan itself (the live sign-in preflight, the reader, the timeout, the abort on unmount) is
// usePopTagScan.ts, shared with the standalone Scan POP Tag screen so there is one implementation.
import { useEffect, useMemo, useRef } from "react";
import type { TapStamp } from "../../lib/offline/types";
import { londonTime, usePopTagScan } from "./usePopTagScan";

export interface PopTagStepProps {
  jobId: string;
  direction: "arrival" | "departure";
  // The reference of THIS job's lift (its ELE number), or null when the job has none to compare.
  expectedRef: string | null;
  // The tap already made for this direction: just recorded, or put back from the sheet's draft.
  stamp: TapStamp | null;
  onStamp: (stamp: TapStamp) => void;
}

// The words for each direction, in one place so the two cards cannot drift apart.
//
// The last sentence of `noSignal` differs on purpose. The arrival card sits near the top of the
// sheet with the Time on site card just below it, so its Arrived field really is "below". The
// leaving card sits at the foot of the sheet, above the customer signature, where the same word
// would send the engineer looking the wrong way.
const WORDS = {
  arrival: {
    region: "POP tag: arriving",
    heading: "Tap the POP tag: arriving",
    intro: "Hold your phone to the sticker on the log card. It sets your arrival time for you.",
    recorded: (time: string) => `Arrival recorded at ${time}`,
    fromPhone: (time: string) => `Arrival time set at ${time} from your phone (the tap could not be recorded).`,
    oneMoreTap: "Tap the tag once more to record your arrival.",
    noSignal:
      "No signal, so the tap itself could not be recorded. Your arrival time has been set from your phone instead. Change it below if it is wrong.",
  },
  departure: {
    region: "POP tag: leaving",
    heading: "Tap the POP tag: leaving",
    intro: "Hold your phone to the sticker on the log card. It sets your leaving time for you.",
    recorded: (time: string) => `Leaving recorded at ${time}`,
    fromPhone: (time: string) => `Leaving time set at ${time} from your phone (the tap could not be recorded).`,
    oneMoreTap: "Tap the tag once more to record your leaving.",
    noSignal:
      "No signal, so the tap itself could not be recorded. Your leaving time has been set from your phone instead. Change it in the Time on site box above if it is wrong.",
  },
} as const;

// Two references name the same lift when they match apart from case and stray spaces. Deliberately
// forgiving: a false "wrong lift" would withhold a time the engineer has genuinely earned.
const sameRef = (a: string, b: string) => a.trim().toUpperCase() === b.trim().toUpperCase();

export function PopTagStep({ jobId, direction, expectedRef, stamp, onStamp }: PopTagStepProps) {
  const words = WORDS[direction];
  // The body is asked for at the moment of the POST, so it always names this card's own direction
  // and the job that is open now.
  const { supported, phase, message, unconfirmed, result, outcome, failure, readAt, scan, cancel } = usePopTagScan({
    body: () => ({ direction, jobId }),
    // Session APF: this card's own button reads "Tap the tag", not "Scan" -- the hook's shared
    // failure copy used to default to naming the standalone Scan POP Tag screen's button regardless,
    // sending an engineer here looking for a control that is not on this screen.
    actionLabel: "tap the tag",
  });

  // The tag that was read belongs to a different lift. The tap has still been recorded, against
  // THAT lift (the server resolves a tag purely from its own token), so it must not set a time on
  // this job, and the engineer is told which lift it was so they can see the mistake for themselves.
  const wrongLift = phase === "done" && result != null && expectedRef != null && !sameRef(result.assetRef, expectedRef);
  const installConfirmed = phase === "done" && outcome === "install_confirmed";

  // The stamp this scan has earned, if any. Worked out from the scan rather than waited for from the
  // parent, so the card shows its recorded state in the same frame the answer arrives instead of
  // flashing back to the prompt for the one render it takes the parent to hand the stamp back down.
  const earnedAt =
    phase === "done" && result != null && outcome != null && !installConfirmed && !wrongLift
      ? result.tappedAt
      : // The sticker WAS read, and only the recording was lost. The phone's own clock at the moment
        // of the read is then the best time anybody has. A tag that is not in use, or a rate limit,
        // earns nothing: the first is not this lift's tag and the second says to wait and try again.
        phase === "failed" && failure === "network" && readAt != null
        ? readAt
        : null;
  const earnedLive = phase === "done";
  const earned = useMemo<TapStamp | null>(() => (earnedAt ? { at: earnedAt, live: earnedLive } : null), [earnedAt, earnedLive]);

  // Hand each earned stamp up ONCE. The parent's callback is read through a ref so that a parent
  // which re-renders on every keystroke (the job sheet does) cannot make this fire again, and the
  // last stamp sent is remembered so a re-render with the same answer sends nothing.
  const onStampRef = useRef(onStamp);
  useEffect(() => {
    onStampRef.current = onStamp;
  });
  const sentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!earned) return;
    const key = `${earned.at}|${earned.live}`;
    if (sentRef.current === key) return;
    sentRef.current = key;
    onStampRef.current(earned);
  }, [earned]);

  // A phone that cannot read a tag gets no card at all, rather than a button that cannot work. The
  // times are set by hand on the Time on site card exactly as they always were. After the hooks,
  // never before them, so the hook order is the same on every render.
  if (!supported) return null;

  const shown = stamp ?? earned;
  const busy = phase === "checking" || phase === "scanning" || phase === "posting";
  // True only for a tap made in THIS visit to the screen whose record does not name the engineer.
  // A stamp put back from a draft carries no such detail, and nothing is claimed about it.
  const withoutName = outcome === "recorded_anonymous" || outcome === "collapsed_other" || outcome === "collapsed_anonymous";

  if (shown) {
    return (
      <section aria-label={words.region} className={`mb-5 rounded-lg border ${shown.live ? "border-ele-green" : "border-ele-card-border"} bg-ele-card p-4`}>
        <p className="font-menu text-[11px] uppercase tracking-wider text-ele-muted">POP tag</p>
        {shown.live ? (
          <p role="status" className="mt-1 font-heading text-base text-ele-green">
            {words.recorded(londonTime(shown.at))}
          </p>
        ) : (
          <p role="status" className="mt-1 text-sm text-ele-strong">
            {words.fromPhone(londonTime(shown.at))}
          </p>
        )}
        {shown.live && earned != null && withoutName ? (
          // The honest second line. The time is good either way, but the engineer pressed a button
          // on their own job sheet and would reasonably assume the record names them.
          <p className="mt-1 text-sm text-ele-body">
            The tap is on file without your name. Your time is set. Please sign the log card as usual.
          </p>
        ) : null}
        {!shown.live && failure === "network" ? (
          <p className="mt-2 text-sm" style={{ color: "var(--ele-amber-text)" }}>
            {words.noSignal}
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <section aria-label={words.region} className="mb-5 rounded-lg border border-ele-card-border bg-ele-card p-4">
      <h2 className="font-heading text-base text-ele-strong">{words.heading}</h2>
      <p className="mt-0.5 font-body text-[13px] text-ele-body">{words.intro}</p>

      {installConfirmed && !wrongLift ? (
        // Clean, not amber: nothing went wrong. The sticker reads, and the platform classified its
        // first scan correctly. The button below is still there for the tap that counts.
        <p role="status" className="mt-3 text-sm text-ele-strong">
          That was this sticker&apos;s first scan. It proves the sticker works and is not counted as a visit. {words.oneMoreTap}
        </p>
      ) : null}

      {wrongLift && result != null && expectedRef != null ? (
        <p role="alert" className="mt-3 text-sm" style={{ color: "var(--ele-amber-text)" }}>
          {installConfirmed
            ? // The other lift's sticker had never been scanned before, so the platform has filed
              // this as that sticker's first scan and not as a visit. Saying "recorded against"
              // here would claim an attendance on a lift that the record deliberately does not show.
              `That tag belongs to ${result.assetRef}, not this job's lift (${expectedRef}). It was that sticker's first scan, which is not counted as a visit. Find this lift's sticker and tap again.`
            : `That tag belongs to ${result.assetRef}, not this job's lift (${expectedRef}). Your tap was recorded against ${result.assetRef}. Find this lift's sticker and tap again.`}
        </p>
      ) : null}

      <button
        type="button"
        onClick={() => void scan()}
        disabled={busy}
        className="mt-3 flex min-h-11 w-full items-center justify-center rounded-ele bg-ele-green px-4 font-heading text-sm tracking-wide text-ele-bg disabled:opacity-60"
      >
        {phase === "checking"
          ? "Checking you are signed in..."
          : phase === "scanning"
            ? "Waiting for the tag..."
            : phase === "posting"
              ? "Recording..."
              : "Tap the tag"}
      </button>

      {phase === "scanning" ? (
        <p className="mt-2 text-sm text-ele-body" role="status">
          Hold the back of your phone against the sticker. Keep the screen on.
        </p>
      ) : null}

      {/* Offered while the sign-in is being confirmed as well as while scanning, as on the Scan POP
          Tag screen and for its reason: a motor room is the worst signal in the building, so a slow
          confirmation is ordinary, and with the button disabled there would otherwise be nothing on
          this card to press. */}
      {phase === "checking" || phase === "scanning" ? (
        <button
          type="button"
          onClick={cancel}
          className="mt-2 flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-card-border px-4 font-menu text-xs tracking-wide text-ele-muted"
        >
          Cancel
        </button>
      ) : null}

      {/* The sign-in could not be confirmed. Amber, because it is neither a success nor a refusal:
          the scan goes ahead, and the recorded state says whether the name reached the record. */}
      {unconfirmed && phase !== "done" ? (
        <p className="mt-2 text-sm" role="status" style={{ color: "var(--ele-amber-text)" }}>
          Could not confirm you are signed in. The scan will go ahead, and you will be told whether your name reached the
          record.
        </p>
      ) : null}

      {message ? (
        <p className="mt-2 text-sm text-ele-body" role="alert">
          {message}
        </p>
      ) : null}

      <p className="mt-2 text-xs text-ele-muted">Cannot tap it? Carry on and set the time yourself.</p>
    </section>
  );
}
